import { formatPeriodLabel } from "./dateRange";

// Top-N de series propias en el header de Ventas antes de colapsar el resto en
// "Otros". Igual a TOP_CLIENTS_COUNT del config de la vista; lo replicamos acá
// para no importar desde `views/` (cruzaría la capa Store → Views).
export const HEADER_CHART_TOP_N = 7;

const OTROS_LABEL = "Otros";

// Deriva las series `byClient` de los gráficos del header a partir de la data
// de la tabla (`accountsData` + `monthHeaders`), filtrando a `labels`. Replica
// la lógica del endpoint `charts` (top-N por acumulado + "Otros") pero del lado
// del cliente, para que el filtro multi-select del header reordene tabla y
// gráficos sin un refetch — y pueda mostrar cualquier item, no sólo los del
// top-N que devuelve el backend.
//
// Cada celda de `accountsData[label][header]` puede ser `{ value, prevYear }`
// (Ventas) o un número plano (Gastos) — `cellValue` soporta ambos para que el
// derive client-side del filtro multi-select sirva en los dos tabs.
function cellValue(cell) {
    if (cell == null) return 0;
    return Number(typeof cell === "object" ? (cell.value ?? 0) : cell);
}

// `accountsData[label][header]` = `{ value, prevYear }` (Ventas) o número
// (Gastos). El shape de retorno matchea `normalizeSalesCharts` en lo que
// consume SalesChartsRow (`monthHeaders` + `byClient`).
export function deriveByClientSeries(accountsData, monthHeaders, labels, topN = HEADER_CHART_TOP_N) {
    const headers = monthHeaders || [];
    const series = (labels || []).map((label) => {
        const row = accountsData[label] || {};
        const values = headers.map((h) => cellValue(row[h]));
        const total = values.reduce((acc, v) => acc + v, 0);
        return { name: label, values, total };
    });

    // Ranking desc por acumulado; desempate por nombre asc para estabilidad
    // (mismo criterio que el backend: key=(-total, name)).
    series.sort((a, b) => (b.total - a.total) || a.name.localeCompare(b.name));

    const top = series.slice(0, topN);
    const rest = series.slice(topN);
    const hasOtros = rest.length > 0;

    const byClient = top.map(({ name, values }) => ({ name, values }));
    if (hasOtros) {
        const otrosValues = headers.map((_, i) =>
            rest.reduce((sum, s) => sum + s.values[i], 0)
        );
        byClient.push({ name: OTROS_LABEL, values: otrosValues });
    }

    return Object.freeze({
        monthHeaders: Object.freeze([...headers]),
        byClient: Object.freeze(byClient.map(Object.freeze)),
        hasOtros,
        topN,
    });
}

// Normaliza la respuesta cruda de `getSalesCharts` al shape que consumen los
// componentes de chart del header de Ventas (stacked + líneas) y el tab KPIs.
// El backend devuelve cada bucket como `{ period: ISO, total_amount|count|amount }`.
// Acá derivamos arrays paralelos alineados a `monthHeaders`, así los charts
// reciben data lista sin repetir el zipping.
//
// Centralizado porque lo usan dos módulos (salesReport y kpisReport) con el
// mismo contrato — cambiar el shape en un solo lugar.
export function normalizeSalesCharts(data, filterPeriod) {
    const {
        by_client: byClientRaw = [],
        client_counts: clientCountsRaw = [],
        avg_tickets: avgTicketsRaw = [],
        has_otros: hasOtros,
        top_n: topN,
        period_range: periodRange,
    } = data;

    // Headers del primer serie disponible; si by_client está vacío caemos a
    // client_counts o a un array vacío.
    const sourcePeriods = (byClientRaw[0]?.values
        || clientCountsRaw
        || []).map((entry) => entry.period);
    const monthHeaders = sourcePeriods.map(
        (iso) => formatPeriodLabel(iso, filterPeriod)
    );

    const byClient = byClientRaw.map((series) => ({
        name: series.name,
        values: series.values.map((entry) => Number(entry.total_amount || 0)),
    }));
    const clientCounts = clientCountsRaw.map((entry) => Number(entry.count || 0));
    const avgTickets = avgTicketsRaw.map((entry) => Number(entry.amount || 0));

    return Object.freeze({
        monthHeaders: Object.freeze(monthHeaders),
        byClient: Object.freeze(byClient.map(Object.freeze)),
        clientCounts: Object.freeze(clientCounts),
        avgTickets: Object.freeze(avgTickets),
        hasOtros: Boolean(hasOtros),
        topN: Number(topN || 0),
        periodRange: Object.freeze(periodRange || {}),
    });
}
